import { EquipmentPlannedDto, EquipmentType, ExecutedMovesTimeRangeDto } from '@operations/app/api'
import { CarrierVisitRefreshEventPayload } from '@operations/app/api/signalRDtos/carrierVisitRefreshEventPayload'
import { WorkInstructionUpsertedEventPayload } from '@operations/app/api/signalRDtos/workInstructionUpsertedEventPayload'
import { EventTypes } from '@operations/messages/eventsTypes'
import { equipmentService } from '@operations/services'
import carrierVisitService from '@operations/services/carrierVisitService'
import { AppStore, IEvent, MessageBus } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { initOperationColumn } from '../helpers/methods'
import {
  ICraneTotalColumn,
  IOperationColumnWithTotal,
  ITotalValue,
  IVesselKPIColumn,
} from '../models/vessel-kpi'

export class VesselVisitKPIUIStore {
  executedMoves: ExecutedMovesTimeRangeDto[] = []
  plannedEquipments: EquipmentPlannedDto[] = []
  vesselVisitId?: number

  constructor(
    private readonly messageBus: MessageBus,
    private readonly appStore: AppStore,
  ) {
    makeObservable(this, {
      executedMoves: observable,
      vesselVisitId: observable,
      plannedEquipments: observable,

      setVesselVisitId: action,

      craneColumns: computed,
      totalColumn: computed,
      totalFooter: computed,
      cranesTotalFooter: computed,
      cranes: computed,
    })

    this.messageBus.subscribeEvent<WorkInstructionUpsertedEventPayload>(
      EventTypes.WorkInstructionUpserted,
      this.handleWorkInstructionUpsertedEvent,
    )

    this.messageBus.subscribeEvent<CarrierVisitRefreshEventPayload>(
      EventTypes.CarrierVisitRefresh,
      this.handleCarrierVisitRefreshEvent,
    )
  }

  public setVesselVisitId(vesselVisitId: number) {
    this.vesselVisitId = vesselVisitId
  }

  public async getExecutedMoves(carrierVisitId: number) {
    await Promise.all([
      this.getexecutedMoves(carrierVisitId),
      this.getPlannedEquipments(carrierVisitId),
    ])
  }

  public get craneColumns() {
    return this.cranes.map(crane => {
      const craneData = this.executedMovesByCrane.get(crane.id) || initOperationColumn()
      return {
        key: crane.id.toString(),
        label: crane.name,
        craneId: crane.id,
        hasInbound: craneData.hasInbound,
        hasOutbound: craneData.hasOutbound,
        hasRestow: craneData.hasRestow,
      }
    })
  }

  public get totalColumn(): IVesselKPIColumn {
    const hasInbound = Array.from(this.executedMovesByCrane.values()).some(data => data.hasInbound)
    const hasOutbound = Array.from(this.executedMovesByCrane.values()).some(
      data => data.hasOutbound,
    )
    const hasRestow = Array.from(this.executedMovesByCrane.values()).some(data => data.hasRestow)

    return {
      key: 'total',
      label: 'Total',
      hasInbound,
      hasOutbound,
      hasRestow,
    }
  }

  public get totalFooter(): ITotalValue {
    const totalInbound = Array.from(this.executedMovesByCrane.values()).reduce(
      (sum, data) => sum + data.inbound,
      0,
    )
    const totalOutbound = Array.from(this.executedMovesByCrane.values()).reduce(
      (sum, data) => sum + data.outbound,
      0,
    )
    const totalRestow = Array.from(this.executedMovesByCrane.values()).reduce(
      (sum, data) => sum + data.restow,
      0,
    )
    const total = totalInbound + totalOutbound + totalRestow

    return {
      inbound: totalInbound,
      outbound: totalOutbound,
      restow: totalRestow,
      total,
    }
  }

  public get cranesTotalFooter(): ICraneTotalColumn[] {
    return this.cranes.map(crane => {
      const craneData = this.executedMovesByCrane.get(crane.id)

      return {
        craneId: crane.id,
        inbound: craneData?.inbound || 0,
        outbound: craneData?.outbound || 0,
        restow: craneData?.restow || 0,
        total: (craneData?.inbound || 0) + (craneData?.outbound || 0) + (craneData?.restow || 0),
      }
    })
  }

  public get cranes() {
    const craneIds = this.executedMoves.flatMap(executedMove =>
      executedMove.executedMoves.map(move => move.equipmentId),
    )

    return this.plannedEquipments.filter(equipment => craneIds.includes(equipment.id))
  }

  private get executedMovesByCrane() {
    const executedMovesByCrane = new Map<number, IOperationColumnWithTotal>()

    for (const executedMove of this.executedMoves) {
      for (const move of executedMove.executedMoves) {
        if (!executedMovesByCrane.has(move.equipmentId)) {
          executedMovesByCrane.set(move.equipmentId, {
            ...initOperationColumn(),
            inbound: 0,
            outbound: 0,
            restow: 0,
            total: 0,
          })
        }

        const craneData = executedMovesByCrane.get(move.equipmentId)!

        if (move.inboundAmount > 0) {
          craneData.hasInbound = true
          craneData.inbound += move.inboundAmount
        }

        if (move.outboundAmount > 0) {
          craneData.hasOutbound = true
          craneData.outbound += move.outboundAmount
        }

        if (move.restowAmount > 0) {
          craneData.hasRestow = true
          craneData.restow += move.restowAmount
        }

        craneData.total += move.inboundAmount + move.outboundAmount + move.restowAmount
      }
    }

    return executedMovesByCrane
  }

  private readonly handleWorkInstructionUpsertedEvent = async (
    event: IEvent<WorkInstructionUpsertedEventPayload>,
  ) => {
    const payload = event.payload

    if (
      payload?.workInstruction.carrierVisitId === this.vesselVisitId &&
      payload?.newJob?.equipmentType === EquipmentType.Sts
    ) {
      await this.getVisitPerformanceWithouLoad(payload.workInstruction.carrierVisitId)
    }
  }

  private readonly handleCarrierVisitRefreshEvent = async (
    event: IEvent<CarrierVisitRefreshEventPayload>,
  ) => {
    const payload = event.payload

    if (payload?.carrierVisits?.some(cv => cv.carrierVisitId === this.vesselVisitId)) {
      await this.getVisitPerformanceWithouLoad(this.vesselVisitId!)
    }
  }

  private async getPlannedEquipments(carrierVisitId: number) {
    const result = await equipmentService.loadPlanned(carrierVisitId)

    runInAction(() => {
      this.plannedEquipments = result
    })
  }

  private async getexecutedMoves(carrierVisitId: number) {
    const result = await carrierVisitService.getExecutedMovesPerCrane(carrierVisitId)

    runInAction(() => {
      this.executedMoves = result.sort((a, b) => dayjs(b.date).diff(dayjs(a.date)))
    })
  }

  private readonly getVisitPerformanceWithouLoad = async (carrierVisitId: number) => {
    await this.appStore.triggerRequestWithoutLoader(
      async () => await this.getExecutedMoves(carrierVisitId),
    )
  }
}
